/**
 * AuthGuard — ensures the user is signed in before rendering children.
 *
 * Uses MSAL's `useMsal` hook to check the interaction / auth state:
 *
 *   INITIALIZING   → show a loading screen while MSAL processes the redirect
 *   NO ACCOUNT     → trigger loginRedirect automatically (spec: no "sign in"
 *                    button — unauthenticated users go straight to Entra)
 *   AUTHENTICATED  → render children
 *
 * The guard also handles the "loading" state during silent token refresh
 * redirects so the app never flickers into an unauthenticated state mid-
 * session.
 */

import React, { useEffect } from 'react';
import {
  useIsAuthenticated,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../lib/msal';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps): React.ReactElement {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // When MSAL is idle and there's no authenticated account, kick off the
  // redirect login automatically. The `inProgress` guard prevents multiple
  // redirect calls firing in strict mode or concurrent renders.
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      void instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress, instance]);

  // MSAL is processing a redirect (coming back from Entra) or performing
  // a silent refresh — show a loading screen so children never mount with
  // a missing token.
  const isBusy =
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.AcquireToken;

  if (isBusy) {
    return <AuthLoadingScreen message="Signing you in…" />;
  }

  // MSAL renders two slots: one for authenticated users, one for guests.
  // We use both so nothing flickers while the redirect is in-flight.
  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        {/* Shown briefly before loginRedirect fires. */}
        <AuthLoadingScreen message="Redirecting to sign-in…" />
      </UnauthenticatedTemplate>
    </>
  );
}

// ---------------------------------------------------------------------------
// Internal loading screen (no imports needed outside this file)
// ---------------------------------------------------------------------------

function AuthLoadingScreen({ message }: { message: string }): React.ReactElement {
  return (
    <div className="auth-screen" role="status" aria-label={message}>
      <div className="auth-screen-inner">
        <div className="spinner" aria-hidden="true" />
        <p className="text-muted">{message}</p>
      </div>
    </div>
  );
}
